import { useMemo } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  DataTable,
  DescriptionList,
  SectionCard,
  type ColumnDef,
  PageWrapper,
} from "@/deps/ui-library";
import { useDiscardJournal, useJournal, usePostJournal } from "@/api/queries";
import {
  ApprovalActions,
  ApprovalCard,
  useApprovalHoldsDraft,
} from "@/components/DocumentApproval";
import type { JournalLine } from "@/api/types";
import { useCan } from "@/app/session";
import { PageHeader } from "@/components/PageHeader";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { JournalStatusBadge } from "./JournalStatusBadge";

const COLUMNS: Array<ColumnDef<JournalLine>> = [
  {
    id: "account",
    header: "Account",
    cell: ({ row: { original } }) => (
      <span className="text-foreground">
        <span className="num">{original.accountCode}</span> {original.accountName}
      </span>
    ),
  },
  {
    id: "party",
    header: "Party",
    cell: ({ row: { original } }) => (
      <span className="text-foreground">{original.partyName ?? "—"}</span>
    ),
  },
  {
    id: "memo",
    header: "Description",
    cell: ({ row: { original } }) => (
      <span className="text-foreground">{original.memo ?? "—"}</span>
    ),
  },
  {
    id: "debit",
    header: "Debit",
    meta: { textAlign: "right" },
    cell: ({ row: { original } }) => (
      <span className="num text-foreground">
        {Number(original.debit) === 0 ? "—" : formatAmount(original.debit)}
      </span>
    ),
  },
  {
    id: "credit",
    header: "Credit",
    meta: { textAlign: "right" },
    cell: ({ row: { original } }) => (
      <span className="num text-foreground">
        {Number(original.credit) === 0 ? "—" : formatAmount(original.credit)}
      </span>
    ),
  },
];

export function JournalDetailPage() {
  const { id } = useParams({ from: "/accounting/journals/$id" });
  const navigate = useNavigate();
  const { data, isPending, error } = useJournal(id);
  const post = usePostJournal();
  const discard = useDiscardJournal();
  const can = useCan();
  const lines = useMemo(() => data?.lines ?? [], [data]);
  const held = useApprovalHoldsDraft("journal", id, data?.status === "draft");

  if (isPending) return <p className="text-muted-foreground text-sm">Loading journal</p>;
  if (error || !data) {
    return <p className="text-bad-text text-sm">That journal could not be loaded.</p>;
  }

  const editable = data.status === "draft" && !held && can("accounting.journals.create");
  const failure = post.error ?? discard.error;

  return (
    <PageWrapper spacing="md" className="p-0">
      <PageHeader
        title={data.number ?? "Journal · Draft"}
        subtitle={`${data.narration} · ${formatDate(data.date)}`}
        actions={
          <span className="flex items-center gap-2">
            <JournalStatusBadge status={data.status} />
            {editable ? (
              <>
                <Button
                  colorScheme="default"
                  variant="ghost"
                  size="sm"
                  disabled={discard.isPending}
                  onClick={() => discard.mutate(id)}
                >
                  Discard
                </Button>
                <Button
                  colorScheme="default"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    void navigate({ to: "/accounting/journals/$id/edit", params: { id } })
                  }
                >
                  Edit
                </Button>
              </>
            ) : null}
            {data.status === "draft" ? (
              <ApprovalActions
                kind="journal"
                id={id}
                canCreate={can("accounting.journals.create")}
                canIssue={can("accounting.journals.post")}
                issuing={post.isPending}
                onIssue={() => post.mutate(id)}
                issueLabel="Post"
                approveLabel="Approve and post"
                workingLabel="Posting"
              />
            ) : null}
          </span>
        }
      />

      {failure ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(failure as Error).message}
        />
      ) : null}

      <ApprovalCard kind="journal" id={id} />

      <SectionCard title="Lines" flush>
        <DataTable
          aria-label="Journal lines"
          size="sm"
          enableSorting={false}
          columns={COLUMNS}
          data={lines}
          getRowId={(line) => `${line.accountId}-${line.debit}-${line.credit}-${line.memo ?? ""}`}
          // Every line, always: a document is not paged.
          pagination={{ mode: "manual", show: false }}
        />
      </SectionCard>

      <SectionCard title="Details">
        <DescriptionList
          items={[
            { label: "Date", value: formatDate(data.date) },
            { label: "Currency", value: data.currency },
            {
              label: "Type",
              value: data.isAdjusting ? (
                <span className="flex items-center gap-1.5">
                  <Badge colorScheme="info" size="sm">
                    Adjusting
                  </Badge>
                  {data.reversesNextYear ? "Reverses next year" : null}
                </span>
              ) : (
                "Ordinary"
              ),
            },
            { label: "Created", value: formatDate(data.createdAt.slice(0, 10)) },
            {
              label: "Posted",
              value: data.postedAt ? formatDate(data.postedAt.slice(0, 10)) : "—",
            },
          ]}
        />
      </SectionCard>
    </PageWrapper>
  );
}
